import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import {useMachine} from '@xstate/react';
import React, {useCallback, useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEvent} from 'analytics/Analytics';
import {distinctDomainToKey} from 'antibodies/AntibodyUtils';
import AntibodyNestedDataCreationModalMachine, {
  SelectDomainPairsInput,
} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationModalMachine';
import {areConstantDomainsFullyDefined} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  AntibodyCreationStepKey,
  ANTIBODY_CREATION_STEPS,
  TranslationParameters,
  NDCSchemaConfig,
  AntibodyDomainInput,
  AntibodyDomainPairInput,
  AntibodyPairAwareCreateInput,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import FinalizePane from 'antibodies/nested-data-creation/panes/FinalizePane';
import GetDomainsPane from 'antibodies/nested-data-creation/panes/GetDomainsPane';
import SelectFormatPane from 'antibodies/nested-data-creation/panes/SelectFormatPane';
import {
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  AnalyticsMetadataForContext,
  AnalyticsModalType,
} from 'constants/AnalyticsConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import useOnMount from 'ui-library/hooks/useOnMount';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

interface NestedCreateAndRegisterAntibodiesModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  initialProjectFolderId?: string | null;
  analyticsMetadata: AnalyticsMetadataForContext;
}

const useStyles = createStyles((theme) => ({
  stepNav: {
    marginBottom: '16px',
  },
}));

export default function NestedCreateAndRegisterAntibodiesModal({
  onRequestClose,
  onRequestSubmit,
  initialProjectFolderId,
  analyticsMetadata,
}: NestedCreateAndRegisterAntibodiesModalProps): JSX.Element {
  const {classes} = useStyles();
  const [ndcModalState, send] = useMachine(AntibodyNestedDataCreationModalMachine, {
    input: {initialProjectFolderId},
  });

  const trackEventWithModule = useCallback(
    (
      event: AnalyticsEvent,
      additionalMetadata: Record<string, unknown> = {},
      includeNDCMetadata: boolean = true
    ): void => {
      const metadata: Record<string, unknown> = {
        ...analyticsMetadata,
        modalType: AnalyticsModalType.ANTIBODY_NESTED_DATA_CREATION,
        ...additionalMetadata,
      };

      if (includeNDCMetadata) {
        metadata.formatId = ndcModalState.context.selectedFormatId;
        metadata.startingDataType = ndcModalState.context.startingDataType;
        metadata.numberOfAntibodies = ndcModalState.context.inputConfig.antibodyCreateInputs.length;
      }

      analytics.trackEventWithModule(event, ANALYTICS_MODULE.ANTIBODIES, metadata);
    },
    [ndcModalState, analyticsMetadata]
  );

  useOnMount(() => {
    trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, {}, false);
  });

  useEffect(() => {
    if (ndcModalState.matches('done')) {
      onRequestSubmit();
      trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL);
    }
  }, [ndcModalState, onRequestSubmit, trackEventWithModule]);

  const getCurrentStep = (): AntibodyCreationStepKey => {
    if (ndcModalState.matches('selectFormat')) {
      return AntibodyCreationStepKey.SELECT_FORMAT;
    }
    if (ndcModalState.matches('defineDomains')) {
      return AntibodyCreationStepKey.DEFINE_DOMAINS;
    }
    if (
      ndcModalState.matches('finalize') ||
      ndcModalState.matches('saving') ||
      ndcModalState.matches('done')
    ) {
      return AntibodyCreationStepKey.FINALIZE;
    }
    return AntibodyCreationStepKey.SELECT_FORMAT;
  };

  const currentStep = getCurrentStep();
  const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
  const statusByStep: {[key in AntibodyCreationStepKey]: StepItemStatus | null} = Object.fromEntries(
    ANTIBODY_CREATION_STEPS.map((step, index) => [
      step.key,
      index <= currentStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    ])
  ) as {[key in AntibodyCreationStepKey]: StepItemStatus | null};

  function handleSubmitButton(): void {
    const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
    if (currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1) {
      send({type: 'createAndRegister'});
    } else {
      send({type: 'next'});
    }
  }

  function handleOnRequestClose(): void {
    onRequestClose();
    trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, {step: currentStep}, true);
  }

  const handlePrevious = (): void => {
    send({type: 'back'});
  };

  function getButtons(): Array<ModalButton> {
    const buttons: Array<ModalButton> = [
      {
        text: 'Cancel',
        onClick: handleOnRequestClose,
        cancel: true,
        pullLeft: true,
        dataTestAttrs: {component: 'NestedCreateAndRegisterAntibodiesModal', key: ModalButtonKey.CANCEL},
      },
    ];
    if (currentStepIndex !== 0) {
      buttons.push({
        text: 'Back',
        onClick: handlePrevious,
        dataTestAttrs: {component: 'NestedCreateAndRegisterAntibodiesModal', key: ModalButtonKey.BACK},
      });
    }
    if (currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1) {
      buttons.push({
        text: 'Create and register',
        submit: true,
        disabled: ndcModalState.context.selectedProjectFolderId == null,
        dataTestAttrs: {component: 'NestedCreateAndRegisterAntibodiesModal', key: ModalButtonKey.SUBMIT},
      });
    } else {
      let isNextDisabled = false;
      // Select Format step
      if (currentStepIndex === 0) {
        isNextDisabled =
          ndcModalState.context.selectedFormatId == null ||
          ndcModalState.context.selectedSchemaId == null ||
          ndcModalState.context.selectedRegistryId == null ||
          ndcModalState.context.ndcSchemaConfig.dnaPairSchemaId == null ||
          ndcModalState.context.ndcSchemaConfig.aaDomainSchemaId == null;
        // Define Domains step
      } else if (currentStepIndex === 1) {
        isNextDisabled =
          // TODO(BNCH-140459): Might need more specific checks here to see that the antibodyCreateInputs are
          // fully specified, depending on isSharingConstantDomains and the actual format
          ndcModalState.context.inputConfig.antibodyCreateInputs.every(
            (input) => input.pairs == null || input.pairs.length === 0
          ) ||
          (ndcModalState.context.inputConfig.isSharingConstantDomains &&
            !areConstantDomainsFullyDefined(ndcModalState.context.inputConfig.constantDomains));
      }
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: isNextDisabled,
        dataTestAttrs: {component: 'NestedCreateAndRegisterAntibodiesModal', key: ModalButtonKey.NEXT},
      });
    }
    return buttons;
  }

  const setSelectedRegistryIdAndDependentConfig = useCallback(
    (registryId: string | null, ndcSchemaConfig: NDCSchemaConfig) => {
      send({
        type: 'selectRegistryAndDependentConfig',
        registryId,
        ndcSchemaConfig,
      });
    },
    [send]
  );

  return (
    <Modal
      title="Create proteins"
      buttons={getButtons()}
      onRequestSubmit={handleSubmitButton}
      onRequestClose={handleOnRequestClose}
      scrollable={true}
      expandHeight={true}
      large={true}
    >
      <div className={classes.stepNav}>
        <StepNav
          steps={ANTIBODY_CREATION_STEPS}
          statusByStep={statusByStep}
          currentStepKey={currentStep}
          onUpdateCurrentStep={undefined}
          variant={StepNavVariant.STACKED}
          size={StepNavSize.FULL_WIDTH}
        />
      </div>
      <div {...getTestProps({component: 'NestedCreateAndRegisterAntibodiesModal', element: 'step-content'})}>
        {currentStep === AntibodyCreationStepKey.SELECT_FORMAT && (
          <SelectFormatPane
            selectedRegistryId={ndcModalState.context.selectedRegistryId}
            setSelectedRegistryIdAndDependentConfig={setSelectedRegistryIdAndDependentConfig}
            selectedFormatId={ndcModalState.context.selectedFormatId}
            setSelectedFormat={(format: AntibodyFormatGraphQL | null) => {
              send({type: 'selectFormat', format});
            }}
            selectedSchemaId={ndcModalState.context.selectedSchemaId}
            setSelectedSchemaId={(schemaId: string | null) => {
              send({type: 'selectSchema', schemaId});
            }}
            startingDataType={ndcModalState.context.startingDataType}
            outputTypes={ndcModalState.context.outputTypes}
          />
        )}
        {currentStep === AntibodyCreationStepKey.DEFINE_DOMAINS && (
          <GetDomainsPane
            pairSchemaId={ndcModalState.context.ndcSchemaConfig.dnaPairSchemaId!}
            constantDomainSchemaId={ndcModalState.context.ndcSchemaConfig.aaDomainSchemaId!}
            selectedPairIds={ndcModalState.context.inputConfig.antibodyCreateInputs
              .map(
                // Assumes we're only creating one pair per antibody, and that it is a DNA domain pair
                (antibodyCreateInput) => antibodyCreateInput.pairs?.[0]?.dnaPairId ?? null
              )
              .filter((dnaPairId): dnaPairId is string => dnaPairId != null)}
            setSelectedPairIds={(selectedPairIds: Array<string>) => {
              // Assumes the domain pair ids are for DNA domain pairs
              const selectDomainPairsInput: SelectDomainPairsInput = selectedPairIds.map(
                (dnaPairId: string) => {
                  // Assumes we only have a single domain pair
                  const antibodyDomainPairInput: AntibodyDomainPairInput = {
                    pairKey: {
                      distinctDomainForPairVh: {
                        label: ndcModalState.context.selectedFormatId === 'builtinabfmt_TCR' ? 'VA' : 'VH',
                        type:
                          ndcModalState.context.selectedFormatId === 'builtinabfmt_TCR'
                            ? AntibodyDomainType.VA
                            : AntibodyDomainType.VH,
                        specificity: null,
                        chainIndex: 0,
                      },
                      distinctDomainForPairVl: {
                        label: ndcModalState.context.selectedFormatId === 'builtinabfmt_TCR' ? 'VB' : 'VL',
                        type:
                          ndcModalState.context.selectedFormatId === 'builtinabfmt_TCR'
                            ? AntibodyDomainType.VB
                            : AntibodyDomainType.VL,
                        specificity: null,
                        chainIndex: 1,
                      },
                    },
                    dnaPairId,
                    aaPairId: null,
                  };
                  return [antibodyDomainPairInput];
                }
              );
              send({type: 'selectDomainPairs', selectDomainPairsInput});
            }}
            selectedFormatId={ndcModalState.context.selectedFormatId!}
            aaDomainTranslationParameters={ndcModalState.context.translationParameters}
            updateAaDomainTranslationParameters={(
              translationParametersUpdate: Partial<TranslationParameters>
            ) => {
              send({
                type: 'setTranslationParameters',
                translationParameters: {
                  ...ndcModalState.context.translationParameters,
                  ...translationParametersUpdate,
                },
              });
            }}
            constantAaDomainEntities={ndcModalState.context.inputConfig.constantDomains}
            selectConstantDomainEntity={(constantDomainEntity: AntibodyDomainInput) => {
              // Find the index of the constant domain being updated to preserve order
              const updatedKey = distinctDomainToKey(constantDomainEntity.componentFormatKey);
              const constantDomainEntities = ndcModalState.context.inputConfig.constantDomains.map(
                (constantDomain) =>
                  distinctDomainToKey(constantDomain.componentFormatKey) === updatedKey
                    ? constantDomainEntity
                    : constantDomain
              );
              send({
                type: 'selectConstantDomains',
                constantDomainEntities,
              });
            }}
            registryId={ndcModalState.context.selectedRegistryId!}
          />
        )}
        {currentStep === AntibodyCreationStepKey.FINALIZE && (
          <FinalizePane
            projectFolderId={ndcModalState.context.selectedProjectFolderId}
            numPairs={ndcModalState.context.inputConfig.antibodyCreateInputs.reduce(
              (sum, input: AntibodyPairAwareCreateInput) => sum + (input.pairs?.length ?? 0),
              0
            )}
            setProjectFolder={(projectFolderId: string) => {
              send({type: 'setProjectFolder', projectFolderId});
            }}
            formatId={ndcModalState.context.selectedFormatId!}
          />
        )}
      </div>
    </Modal>
  );
}
